/**
 * TripMapView - Google Maps 顯示當天行程 Marker 與路線
 * 使用 @vis.gl/react-google-maps，只渲染有真實座標的行程
 */

import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import {
  Map,
  AdvancedMarker,
  useMap,
  useMapsLibrary,
  useApiLoadingStatus,
  useAdvancedMarkerRef,
  APILoadingStatus,
} from '@vis.gl/react-google-maps';
import type { Activity } from '../../types/activity';
import PlaceInfoWindow from './PlaceInfoWindow';
import UserLocationLayer from './UserLocationLayer';

const DEFAULT_CENTER = { lat: 25.0478, lng: 121.5319 }; // Taipei
const DEFAULT_ZOOM = 13;

interface TripMapViewProps {
  activities: Activity[];
  selectedActivityId?: string | null;
  onMarkerClick?: (activityId: string) => void;
  onMapClick?: () => void;
  tripId?: string;
  disableInfoWindow?: boolean;
  /** 沒有登入權限可導到行程詳情頁時（例如分享頁），改用這個開啟唯讀詳情 */
  onViewDetails?: (activity: Activity) => void;
  /** 是否顯示行程順序編號跟連線；關閉時只顯示地點標點。預設 true */
  showRoute?: boolean;
}

interface ActivityMarkerProps {
  activity: Activity;
  index: number;
  isSelected: boolean;
  showOrder: boolean;
  onClick: () => void;
  onMarkerReady: (id: string, marker: google.maps.marker.AdvancedMarkerElement | null) => void;
}

// 獨立成元件是為了讓每個 marker 都能透過 useAdvancedMarkerRef 拿到自己的 DOM 實例，
// 供 InfoWindow 用 anchor 定位，避免卡片蓋住圖標（anchor 會依 marker 實際大小自動算偏移量）
function ActivityMarker({ activity, index, isSelected, showOrder, onClick, onMarkerReady }: ActivityMarkerProps) {
  const [markerRef, marker] = useAdvancedMarkerRef();

  useEffect(() => {
    onMarkerReady(activity.id, marker);
  }, [marker, activity.id, onMarkerReady]);

  return (
    <AdvancedMarker ref={markerRef} position={{ lat: activity.lat!, lng: activity.lng! }} onClick={onClick}>
      {showOrder ? (
        // 手機版縮小成 30px，避免數字標點在小螢幕上遮住太多地圖；桌面版（lg:）維持 44px
        <div
          className="size-[30px] text-sm border-2 lg:size-11 lg:text-lg lg:border-[2.5px]"
          style={{
            background: isSelected ? '#0aa8c8' : '#0dccf2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 700,
            fontFamily: 'Inter, sans-serif',
            boxShadow: '0 2px 10px rgba(0,188,212,0.45)',
            borderStyle: 'solid',
            borderColor: '#fff',
            cursor: 'pointer',
          }}
        >
          {index + 1}
        </div>
      ) : activity.dayIndex === null ? (
        // 未排期：同樣用主題色，但改成白底空心圈，和已排期的實心點區分
        <div
          style={{
            width: 20,
            height: 20,
            background: isSelected ? '#cff5fc' : '#fff',
            borderRadius: '50%',
            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
            border: `4px solid ${isSelected ? '#0aa8c8' : '#0dccf2'}`,
            cursor: 'pointer',
          }}
        />
      ) : (
        <div
          style={{
            width: 20,
            height: 20,
            background: isSelected ? '#0aa8c8' : '#0dccf2',
            borderRadius: '50%',
            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
            border: '3px solid #fff',
            cursor: 'pointer',
          }}
        />
      )}
    </AdvancedMarker>
  );
}

// 內部元件：需要在 Map 的 context 內才能使用 useMap / useMapsLibrary
function MapContent({
  activities,
  selectedActivityId,
  onMarkerClick,
  tripId,
  disableInfoWindow,
  onViewDetails,
  showRoute = true,
}: TripMapViewProps) {
  const map = useMap();
  const mapsLib = useMapsLibrary('maps');
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const [markerInstances, setMarkerInstances] = useState<
    Record<string, google.maps.marker.AdvancedMarkerElement | null>
  >({});

  const handleMarkerReady = useCallback(
    (id: string, marker: google.maps.marker.AdvancedMarkerElement | null) => {
      setMarkerInstances((prev) => (prev[id] === marker ? prev : { ...prev, [id]: marker }));
    },
    []
  );

  const activitiesWithCoords = activities.filter(
    (a) => a.lat != null && a.lng != null
  );

  // 呼叫端常常每次 render 都傳新的 activities 陣列（內容沒變），若路線/視野 effect 直接依賴
  // activitiesWithCoords，每次 re-render 都會重畫路線並 fitBounds，把使用者拖曳過的地圖拉回去。
  // 改成只依賴「地點 id + 座標」組成的字串，內容真的變了才重算。
  const pointsKey = JSON.stringify(activitiesWithCoords.map((a) => [a.id, a.lat, a.lng]));
  const points = useMemo(
    () => (JSON.parse(pointsKey) as [string, number, number][]).map(([id, lat, lng]) => ({ id, lat, lng })),
    [pointsKey]
  );

  // 更新 Polyline
  useEffect(() => {
    if (!map || !mapsLib) return;

    // 清除舊 polyline
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    if (showRoute && points.length > 1) {
      const path = points.map((p) => ({ lat: p.lat, lng: p.lng }));
      polylineRef.current = new mapsLib.Polyline({
        path,
        strokeColor: '#0dccf2',
        strokeWeight: 3.5,
        strokeOpacity: 0.85,
        map,
      });
    }

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [map, mapsLib, points, showRoute]);

  // 調整地圖視野：選取特定行程時聚焦該點，否則依全部行程決定範圍
  useEffect(() => {
    if (!map || !mapsLib) return;

    const selected = selectedActivityId
      ? points.find((p) => p.id === selectedActivityId)
      : null;

    if (selected) {
      map.panTo({ lat: selected.lat, lng: selected.lng });
      map.setZoom(16);
      return;
    }

    if (points.length === 0) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
    } else if (points.length === 1) {
      map.setCenter({ lat: points[0].lat, lng: points[0].lng });
      map.setZoom(14);
    } else {
      const bounds = new google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
    }
  }, [map, mapsLib, points, selectedActivityId]);

  const handleMarkerClick = useCallback(
    (activityId: string) => {
      onMarkerClick?.(activityId);
    },
    [onMarkerClick]
  );

  const selectedActivity = selectedActivityId
    ? activitiesWithCoords.find((a) => a.id === selectedActivityId) ?? null
    : null;

  return (
    <>
      {activitiesWithCoords.map((activity, index) => (
        <ActivityMarker
          key={activity.id}
          activity={activity}
          index={index}
          isSelected={selectedActivityId === activity.id}
          showOrder={showRoute}
          onClick={() => handleMarkerClick(activity.id)}
          onMarkerReady={handleMarkerReady}
        />
      ))}

      {selectedActivity && !disableInfoWindow && (
        <PlaceInfoWindow
          activity={selectedActivity}
          anchor={markerInstances[selectedActivity.id] ?? null}
          onClose={() => onMarkerClick?.('')}
          tripId={tripId}
          onViewDetails={onViewDetails}
        />
      )}
    </>
  );
}

export default function TripMapView({
  activities,
  selectedActivityId,
  onMarkerClick,
  onMapClick,
  tripId,
  disableInfoWindow,
  onViewDetails,
  showRoute,
}: TripMapViewProps) {
  const loadingStatus = useApiLoadingStatus();

  if (loadingStatus === APILoadingStatus.FAILED) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-100">
        <div className="text-center px-6">
          <svg className="w-10 h-10 text-slate-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <p className="text-slate-500 text-sm">地圖暫時無法使用</p>
          <p className="text-slate-400 text-xs mt-1">行程列表仍可正常瀏覽</p>
        </div>
      </div>
    );
  }

  return (
    <Map
      style={{ width: '100%', height: '100%' }}
      defaultCenter={DEFAULT_CENTER}
      defaultZoom={DEFAULT_ZOOM}
      mapId="tripway-map"
      gestureHandling="greedy"
      disableDefaultUI={true}
      onClick={onMapClick}
    >
      <MapContent
        activities={activities}
        selectedActivityId={selectedActivityId}
        onMarkerClick={onMarkerClick}
        tripId={tripId}
        disableInfoWindow={disableInfoWindow}
        onViewDetails={onViewDetails}
        showRoute={showRoute}
      />
      <UserLocationLayer />
    </Map>
  );
}
